import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import InfoList from 'components/InfoList';
import FirmSection from 'components/FirmSection';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import FirmPortfolioEditCriteriaForm from 'scenes/Form/components/FirmPortfolioEditCriteriaForm';
import FirmPortfolioCreateUpdateItemForm from 'scenes/Form/components/FirmPortfolioCreateUpdateItemForm';
import FirmPortfolioFirms from 'scenes/FirmPortfolio/components/FirmPortfolioFirms';

import { POPUP_STYLES } from 'constants/popup-styles';

import './styles.css';

const FirmPortfolio = props => {
  const {
    user,
    firmId,
    isEditMode,
    grids: { current, realized },
    criteria = {},
    criteria: { Criteria, Sector, Size, Style, Geography },
    onClickDealButton,
    onEditPortfolioItem,
    onDeletePortfolioItem,
    onAddPortfolioItem,
    onCriteriaUpdate,
    onClickPage,
    onAddPortfolioComment,
    onRemovePortfolioComment,
    onClickShowPortfolioPreviousComments,
  } = props;

  const criteriaSection = useMemo(
    () => (
      <>
        {Criteria && (
          <p className="firm-portfolio__card-description">{Criteria}</p>
        )}

        <div className="firm-portfolio__content">
          <InfoList
            objectList={{
              sector: { title: 'Sector', value: Sector || '' },
              size: { title: 'Size', value: Size || '' },
              style: { title: 'Style', value: Style || '' },
              geography: { title: 'Geography', value: Geography || '' },
            }}
          />
        </div>
      </>
    ),
    [criteria]
  );

  const editCriteria = () => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: {
        ...POPUP_STYLES.formPopupStyles,
        maxWidth: '540px',
      },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { values: { ...criteria, id: firmId } },
      form: (actions, params) => (
        <FirmPortfolioEditCriteriaForm actions={actions} params={params} />
      ),
    }).then(
      values => onCriteriaUpdate(values),
      response => ResponseErrorDialog({ response })
    );
  };

  const addCurrent = () => addItem('Current');
  const addRealized = () => addItem('Realized');

  const addItem = type => {
    FormDialog({
      title: `Add Firm`,
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: POPUP_STYLES.formInnerStyles,
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { id: firmId, type },
      form: (actions, params) => (
        <FirmPortfolioCreateUpdateItemForm actions={actions} params={params} />
      ),
    }).then(
      response => onAddPortfolioItem(response),
      response => ResponseErrorDialog({ response })
    );
  };

  const editItem = id => {
    FormDialog({
      title: 'Edit',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: POPUP_STYLES.formInnerStyles,
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { id: firmId, emId: id },
      form: (actions, params) => (
        <FirmPortfolioCreateUpdateItemForm actions={actions} params={params} />
      ),
    }).then(
      response => onEditPortfolioItem(response),
      response => ResponseErrorDialog({ response })
    );
  };

  return (
    <article className="firm-portfolio">
      <FirmSection
        title="Criteria"
        controls={isEditMode && [{ caption: 'Edit', handler: editCriteria }]}
      >
        {criteriaSection}
      </FirmSection>
      <FirmPortfolioFirms
        user={user}
        isEditMode={isEditMode}
        title="Current"
        type="Current"
        grid={current}
        onClickDealButton={onClickDealButton}
        onClickAdd={addCurrent}
        onClickEdit={editItem}
        onClickDelete={onDeletePortfolioItem}
        onClickPage={onClickPage}
        onAddComment={onAddPortfolioComment}
        onRemovePortfolioComment={onRemovePortfolioComment}
        onClickShowPreviousComments={onClickShowPortfolioPreviousComments}
      />
      <FirmPortfolioFirms
        user={user}
        isEditMode={isEditMode}
        title="Realized"
        type="Realized"
        grid={realized}
        onClickDealButton={onClickDealButton}
        onClickAdd={addRealized}
        onClickEdit={editItem}
        onClickPage={onClickPage}
        onClickDelete={onDeletePortfolioItem}
        onAddComment={onAddPortfolioComment}
        onRemovePortfolioComment={onRemovePortfolioComment}
        onClickShowPreviousComments={onClickShowPortfolioPreviousComments}
      />
    </article>
  );
};

FirmPortfolio.propTypes = {
  user: PropTypes.object.isRequired,
  firmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isEditMode: PropTypes.bool.isRequired,
  criteria: PropTypes.shape({
    Criteria: PropTypes.string,
    Sector: PropTypes.string,
    Size: PropTypes.string,
    Style: PropTypes.string,
    Geography: PropTypes.string,
  }),
  grids: PropTypes.object,
  onClickDealButton: PropTypes.func,
  onEditPortfolioItem: PropTypes.func,
  onDeletePortfolioItem: PropTypes.func,
  onAddPortfolioItem: PropTypes.func,
  onCriteriaUpdate: PropTypes.func,
  onClickPage: PropTypes.func.isRequired,
  onAddPortfolioComment: PropTypes.func,
  onClickShowPortfolioPreviousComments: PropTypes.func,
};

export default FirmPortfolio;
